import { Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { ApiService } from './api.service';

@Component({
  selector: 'app-admin-leave-requests',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink],
  template: `
    <div class="min-h-full overflow-y-auto admin-premium-bg">
      <div class="max-w-[1500px] mx-auto p-5 space-y-5">
        <header class="flex items-start justify-between gap-4">
          <div>
            <h1 class="text-xl font-semibold text-slate-950 tracking-tight">Leave Requests</h1>
            <p class="text-slate-500 text-xs mt-1">Review staff leave applications and notify employees by email.</p>
          </div>
        </header>

        <section class="grid grid-cols-1 md:grid-cols-4 gap-3">
          @for (card of summaryCards(); track card.label) {
            <div class="bg-white rounded-xl border border-[#E7EEF5] px-4 py-3 shadow-sm">
              <p class="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{{ card.label }}</p>
              <p class="text-xl font-semibold text-slate-950 mt-1">{{ card.value }}</p>
              <p class="text-[11px] text-slate-500 mt-1">{{ card.help }}</p>
            </div>
          }
        </section>

        <section class="bg-white rounded-xl border border-[#E7EEF5] shadow-sm overflow-hidden">
          <div class="px-4 py-3 border-b border-slate-100 flex items-center gap-2 flex-wrap">
            <div class="relative flex-1 min-w-[260px] max-w-md">
              <i class="ti ti-search absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-[16px]"></i>
              <input [(ngModel)]="filters.search" (input)="loadRequests()" type="text" placeholder="Search employee, type or reason"
                class="w-full h-9 pl-9 pr-3 rounded-lg text-xs border border-slate-200 bg-slate-50 focus:bg-white focus:outline-none focus:border-[#1A6FA6]">
            </div>
            <select [(ngModel)]="filters.status" (change)="loadRequests()" class="h-9 rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-600">
              <option value="">All status</option>
              @for (status of statuses; track status) { <option [value]="status">{{ status }}</option> }
            </select>
            <select [(ngModel)]="filters.leave_type" (change)="loadRequests()" class="h-9 rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-600">
              <option value="">All leave types</option>
              @for (type of leaveTypes; track type) { <option [value]="type">{{ type }}</option> }
            </select>
          </div>

          <div class="overflow-x-auto min-h-[420px]">
            <table class="w-full text-left text-sm">
              <thead>
                <tr class="bg-slate-50/80 border-b border-slate-100">
                  <th class="px-4 py-2.5 text-[10px] font-bold text-slate-400 uppercase tracking-widest">Employee</th>
                  <th class="px-4 py-2.5 text-[10px] font-bold text-slate-400 uppercase tracking-widest">Leave</th>
                  <th class="px-4 py-2.5 text-[10px] font-bold text-slate-400 uppercase tracking-widest">Dates</th>
                  <th class="px-4 py-2.5 text-[10px] font-bold text-slate-400 uppercase tracking-widest text-center">Status</th>
                  <th class="px-4 py-2.5 text-[10px] font-bold text-slate-400 uppercase tracking-widest text-right">Action</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-slate-100">
                @for (request of requests(); track request.id) {
                  <tr class="align-top hover:bg-slate-50">
                    <td class="px-4 py-3">
                      <p class="font-semibold text-slate-900 text-sm">{{ request.employee_name }}</p>
                      <p class="text-[10px] text-slate-400">{{ request.employee_email }}</p>
                      <p class="text-[10px] text-slate-400">{{ request.employee_phone || '-' }}</p>
                    </td>
                    <td class="px-4 py-3">
                      <p class="font-semibold text-slate-900 text-sm">{{ request.leave_type }}</p>
                      <p class="text-[10px] text-slate-400">{{ request.is_half_day == 1 ? 'Half day included' : 'Full day request' }}</p>
                    </td>
                    <td class="px-4 py-3 text-xs text-slate-600 whitespace-nowrap">
                      <p>{{ request.start_date | date:'dd MMM yyyy' }} - {{ request.end_date | date:'dd MMM yyyy' }}</p>
                      <p class="text-[10px] text-slate-400">{{ request.total_days }} day(s)</p>
                      @if (request.expected_due_or_adoption_date) {
                        <p class="text-[10px] text-slate-400">Due/adoption: {{ request.expected_due_or_adoption_date | date:'dd MMM yyyy' }}</p>
                      }
                    </td>
                    <td class="px-4 py-3 text-center">
                      <span class="px-2.5 py-1 rounded-md text-[10px] font-semibold uppercase border" [ngClass]="statusBadgeClass(request.status)">{{ request.status }}</span>
                      @if (request.decided_at) {
                        <p class="text-[10px] text-slate-400 mt-1">{{ request.decided_by_name || 'Admin' }}<br>{{ request.decided_at | date:'dd MMM yyyy' }}</p>
                      }
                    </td>
                    <td class="px-4 py-3 text-right">
                      <a [routerLink]="['/admin/operations/leave-requests', request.id]" class="inline-flex h-8 items-center gap-1.5 rounded-lg border border-sky-100 bg-sky-50 px-3 text-xs font-bold text-[#1A6FA6] hover:bg-sky-100">
                        View details <i class="ti ti-arrow-right"></i>
                      </a>
                    </td>
                  </tr>
                }
                @if (!isLoading() && requests().length === 0) {
                  <tr>
                    <td colspan="5" class="py-16 text-center text-slate-400">
                      <i class="ti ti-calendar-off text-4xl mb-3 opacity-25"></i>
                      <p class="font-semibold text-sm">No leave requests found.</p>
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>

  `
})
export class AdminLeaveRequestsComponent implements OnInit {
  private api = inject(ApiService);
  requests = signal<any[]>([]);
  isLoading = signal(false);
  filters: any = { search: '', status: 'Pending', leave_type: '' };
  statuses = ['Pending', 'Approved', 'Declined', 'Cancelled'];
  leaveTypes = ['Annual Leave', 'Sick Leave', 'Bereavement Leave', 'Family Violence Leave', 'Parental Leave', 'Jury Service Leave'];

  ngOnInit() {
    this.loadRequests();
  }

  loadRequests() {
    this.isLoading.set(true);
    this.api.getLeaveRequests(this.filters).subscribe({
      next: (res: any) => {
        this.requests.set(res.data?.leave_requests || []);
        this.isLoading.set(false);
      },
      error: () => this.isLoading.set(false)
    });
  }

  summaryCards() {
    const requests = this.requests();
    return [
      { label: 'Pending', value: requests.filter(item => item.status === 'Pending').length, help: 'Awaiting admin action' },
      { label: 'Approved', value: requests.filter(item => item.status === 'Approved').length, help: 'In the current view' },
      { label: 'Declined', value: requests.filter(item => item.status === 'Declined').length, help: 'In the current view' },
      { label: 'People', value: new Set(requests.map(item => item.employee_id)).size, help: 'Staff represented' }
    ];
  }

  statusBadgeClass(status: string): string {
    if (status === 'Approved') return 'text-emerald-700 bg-emerald-50 border-emerald-100';
    if (status === 'Declined' || status === 'Cancelled') return 'text-rose-700 bg-rose-50 border-rose-100';
    return 'text-amber-700 bg-amber-50 border-amber-100';
  }
}
